import FilterChip from 'LuxKit/Chips/FilterChip'
import Heading from 'LuxKit/Typography/Heading'
import React, { useContext, useMemo, useState } from 'react'
import Image from 'components/Common/Image/Image'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import useCarHireDestinationPriceInfo from 'hooks/CarHire/useCarHireDestinationPriceInfo'
import FormatCurrency from 'components/Common/FormatCurrency'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { rem } from 'polished'
import { GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { scrollToId } from 'lib/window/scrollUtils'
import NestableClickable from 'components/Common/NestableClickable'
import { TRENDING_DESTINATIONS, trendingDestinationToSearchLocation, trendingDestinationsToImageId } from '../CarHireHardcodedLists'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const ColumnedList = styled(VerticalSpacer)`
  column-count: 3;
`

const LoadingWrapper = styled(Group)`
  height: ${rem(140)};
`

const MobileScroller = styled(Group)`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`
const CondensedGroup = styled(Group)`
  width: ${rem(280)};
  padding-right: ${rem(12)};
`

interface Props {
  scrollId: string
}

function CarHirePageTrendingDestinations(props: Props) {
  const { scrollId } = props
  const tabs = ['Worldwide', 'Australia']
  const [activeTab, setActiveTab] = useState(tabs[0])

  const params = useMemo<App.CarHireDestinationPriceInfoParams>(() => {
    return {
      key: activeTab,
      cities: TRENDING_DESTINATIONS[activeTab.toUpperCase()].cities,
      airports: TRENDING_DESTINATIONS[activeTab.toUpperCase()].airports,
      limit: 10,
    }
  },
  [activeTab])

  const list = useCarHireDestinationPriceInfo(params)

  const dispatch = useContext(GlobalSearchDispatchContext)

  const onClick = (key: string) => {
    const searchItem = trendingDestinationToSearchLocation.get(key.toUpperCase())
    if (searchItem) {
      dispatch({
        type: GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem,
      })
      scrollToId(scrollId, -70)
    }
  }

  return (
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading3">Browse by trending <i>destinations</i></Heading>
        <VerticalSpacer gap={40}>
          <Group direction="horizontal" gap={8}>
            {tabs.map((tabItem) => (
              <FilterChip selected={tabItem === activeTab} onClick={() => setActiveTab(tabItem)} key={tabItem} size="medium">
                {tabItem}
              </FilterChip>
            ))}
          </Group>
          {list.fetching &&
            <LoadingWrapper gap={24} direction="vertical" fullWidth horizontalAlign="center" verticalAlign="center">
              <LoadingAnimation />
            </LoadingWrapper>}
          {/* desktop */}
          <CSSBreakpoint min="tablet">
            {!list.fetching && list.destinations.length > 0 &&
              <ColumnedList gap={16}>
                {list.destinations.filter((destination) => destination.locationMinimumPrice > 0).map((destination) => (
                  <NestableClickable onClick={() => onClick(destination.key)} key={destination.locationName}>
                    <Group gap={16} direction="horizontal">
                      <Image width={68} height={68} id={trendingDestinationsToImageId.get(destination.key.toUpperCase())} />
                      <div>
                        <BodyTextBlock variant="large" weight="bold">
                          {trendingDestinationToSearchLocation.get(destination.key.toUpperCase())?.format.mainText}
                        </BodyTextBlock>
                        <BodyTextBlock variant="small">
                          Starting from <FormatCurrency format="roundedDollar" value={destination.locationMinimumPrice} />/day
                        </BodyTextBlock>
                      </div>
                    </Group>
                  </NestableClickable>
                ))}
              </ColumnedList>}
          </CSSBreakpoint>
          {/* mobile */}
          <CSSBreakpoint max="mobile">
            {!list.fetching && list.destinations.length > 0 &&
              <MobileScroller direction="horizontal" gap={24}>
                {list.destinations.filter((destination) => destination.locationMinimumPrice > 0).map((destination) => (
                  <NestableClickable onClick={() => onClick(destination.key)} key={destination.locationName}>
                    <CondensedGroup gap={16} direction="horizontal">
                      <Image width={68} height={68} id={trendingDestinationsToImageId.get(destination.key.toUpperCase())} />
                      <div>
                        <BodyTextBlock variant="large" weight="bold">
                          {trendingDestinationToSearchLocation.get(destination.key.toUpperCase())?.format.mainText}
                        </BodyTextBlock>
                        <BodyTextBlock variant="small">
                          Starting from <FormatCurrency format="roundedDollar" value={destination.locationMinimumPrice} />/day
                        </BodyTextBlock>
                      </div>
                    </CondensedGroup>
                  </NestableClickable>
                ))}
              </MobileScroller>}
          </CSSBreakpoint>
        </VerticalSpacer>
      </VerticalSpacer>
    </LayoutContainer>

  )
}

export default CarHirePageTrendingDestinations
